
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Settings,
  Activity,
  Briefcase,
  ShieldCheck,
  TrendingUp,
  Sparkles,
  Wifi,
  WifiOff,
  RefreshCw,
  Book,
  Globe,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useStore } from '../hooks/useStore.ts';
import { useRole } from '../hooks/useRole.ts';
import { api } from '../services/api.ts';
import { SyncManager } from '../services/syncService.ts';

const THEMES: Record<string, any> = {
  zen: {
    n: '#e4e4e7', // Darker background (zinc-200) for contrast with white panels
    b: '#d4d4d8', // Darker borders (zinc-300)

    text: '#09090b',
    prim: '#ffffff',
    accent: '#27272a',
    sidebar: '#ffffff'
  },
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const settingsList = useStore(() => api.settings.list(), [], 'settings');
  const user = settingsList?.[0];
  const { isOwner } = useRole();
  const themeKey = user?.theme || 'zen';
  const [syncStatus, setSyncStatus] = React.useState<'Online' | 'Offline' | 'Syncing'>('Online');

  useEffect(() => {
    // Theme logic
    const tTheme = THEMES[themeKey] || THEMES.zen;
    const root = document.documentElement.style;
    root.setProperty('--lc-bg', tTheme.n);
    root.setProperty('--lc-border', tTheme.b);
    root.setProperty('--lc-text', tTheme.text);
    root.setProperty('--lc-primary', tTheme.prim);
    root.setProperty('--lc-accent', tTheme.accent);
    root.setProperty('--lc-sidebar', tTheme.sidebar);

    // Sync status listeners
    const unsubscribe = SyncManager.getInstance().subscribe(setSyncStatus);
    return () => { unsubscribe(); };
  }, [themeKey]);

  const navClass = (path: string) => {
    const location = useLocation();
    const active = location.pathname === path || (path === '/projects' && location.pathname.startsWith('/projects'));
    return clsx(
      "flex items-center gap-3 px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-all rounded-xl",
      active
        ? "bg-black text-white shadow-lg"
        : "text-zinc-400 hover:text-black hover:bg-zinc-200/50"
    );
  };

  const getSyncStatusLabel = () => {
    switch (syncStatus) {
      case 'Syncing': return t('common.syncing_dots');
      case 'Online': return t('common.cloud_active');
      case 'Offline': return t('common.offline_mode');
    }
  };


  const { i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ko' : 'en';
    i18n.changeLanguage(newLang);
  };

  return (
    <div className="flex h-screen overflow-hidden transition-colors duration-500" style={{ backgroundColor: 'var(--lc-bg)', color: 'var(--lc-text)' }}>
      <aside className="w-64 flex flex-col border-r shrink-0 overflow-hidden" style={{ backgroundColor: 'var(--lc-sidebar)', borderColor: 'var(--lc-border)' }}>
        <div className="p-8 mb-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-8 h-8 bg-black rounded-lg text-white shadow-lg flex items-center justify-center font-black text-[11px] shrink-0 group-hover:scale-110 transition-transform">
              GF
            </div>
            <h1 className="text-lg font-black tracking-tighter leading-none">
              <span className="text-black">Project</span>
              <span className="text-zinc-400">Controls Advisor</span>
            </h1>
          </Link>
          <button
            onClick={toggleLanguage}
            className="w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-400 hover:text-black transition-colors"
            title="Switch Language (EN/KO)"
          >
            <Globe size={16} />
          </button>
        </div>

        <div className="flex-1 px-4 flex flex-col justify-between pb-10 overflow-y-auto custom-scrollbar">
          <nav className="space-y-1">
            <div className="text-[8px] font-black uppercase text-zinc-300 tracking-[0.2em] mb-4 ml-4">{t('nav.strategic')}</div>
            <Link to="/" className={navClass('/')}><TrendingUp size={16} /><span>{t('nav.profit_center')}</span></Link>
            <Link to="/projects" className={navClass('/projects')}><Briefcase size={16} /><span>{t('nav.job_registry')}</span></Link>

            {isOwner && (
              <div className="pt-8 mb-2">
                <div className="text-[8px] font-black uppercase text-zinc-300 tracking-[0.2em] mb-4 ml-4">{t('nav.intelligence')}</div>
                <button
                  onClick={() => document.dispatchEvent(new CustomEvent('toggle-ai-chatbot'))}
                  className="w-full flex items-center gap-3 px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-all rounded-xl text-amber-600 hover:text-black hover:bg-amber-50 border border-transparent hover:border-amber-200"
                >
                  <Sparkles size={16} className="text-amber-500" />
                  <span>{t('nav.ai_assistant')}</span>
                </button>
              </div>
            )}

            <div className="pt-4 mb-2">
              <div className="text-[8px] font-black uppercase text-zinc-300 tracking-[0.2em] mb-4 ml-4">{t('nav.infrastructure')}</div>
              <Link to="/knowledge-base" className={navClass('/knowledge-base')}><Book size={16} /><span>{t('nav.knowledge_base') || 'Knowledge Base'}</span></Link>
              <Link to="/settings" className={navClass('/settings')}><Settings size={16} /><span>{t('nav.system_config')}</span></Link>
            </div>
          </nav>

          <div className="p-5 bg-zinc-50/80 rounded-[2rem] border border-zinc-100 flex flex-col gap-3 shrink-0 mt-8">
            <div className="w-7 h-7 rounded-lg bg-black text-white flex items-center justify-center font-black text-[10px] shrink-0 shadow-sm border border-zinc-800">
              {user?.userName?.[0] || 'O'}
            </div>
            <div className="flex flex-col min-w-0">
              <div className="text-[10px] font-black text-black leading-tight truncate">{user?.userName || t('common.operator')}</div>
              <div className="text-[7px] uppercase font-black text-zinc-400 tracking-[0.15em] truncate mt-0.5">{user?.userTitle || t('common.operations_control')}</div>
            </div>
          </div>
        </div>

        <div className="p-4 bg-zinc-900 mx-4 mb-4 rounded-xl flex items-center justify-between shadow-lg mt-auto shrink-0">
          <div className="flex items-center gap-2">
            {syncStatus === 'Syncing' && <RefreshCw size={12} className="text-zinc-400 animate-spin" />}
            {syncStatus === 'Online' && <Wifi size={12} className="text-emerald-500" />}
            {syncStatus === 'Offline' && <WifiOff size={12} className="text-rose-500" />}
            <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400">
              {getSyncStatusLabel()}
            </span>
          </div>
          <div className={`w-2 h-2 rounded-full ${syncStatus === 'Online' ? 'bg-emerald-500' : syncStatus === 'Syncing' ? 'bg-amber-500 animate-pulse' : 'bg-rose-500'}`}></div>
        </div>
        <div className="px-8 pb-4 text-[9px] text-zinc-500 font-mono text-center opacity-50">
          v1.2 (KB+RAG)
        </div>
      </aside>

      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        <div className="flex-1 overflow-auto p-8 md:p-12 relative custom-scrollbar">
          {children}
        </div>
      </main>
    </div >
  );
};
